import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const STANDARD_FIELDS = [
  { key: "companyName", label: "Company Name" },
  { key: "website", label: "Website" },
  { key: "boothNumber", label: "Booth Number" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "industry", label: "Industry" },
  { key: "description", label: "Description" },
];
const FIELD_SUGGESTIONS = {
  companyName: ["company", "companyname", "exhibitor", "exhibitorname"],
  website: ["website", "url", "domain"],
  boothNumber: ["booth", "boothnumber", "stand"],
  city: ["city"],
  state: ["state", "region", "province"],
  country: ["country"],
  industry: ["industry", "category", "sector"],
  description: ["description", "about", "profile"],
};
const NAME_LIKE_COLUMN_TERMS = [
  "company",
  "companyname",
  "name",
  "exhibitor",
  "organization",
  "organisation",
  "business",
  "account",
];
const MAX_IMPORT_ROWS = 10000;

function getFileType(file) {
  const extension = file?.name?.split(".").pop()?.toLowerCase();
  if (extension === "csv") return "CSV";
  if (extension === "xlsx") return "XLSX";
  return file?.type || "Unknown";
}

function formatFileSize(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function normalizeHeader(header) {
  return String(header ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function hasNameLikeColumn(headers) {
  return headers.some((header) => {
    const normalized = normalizeHeader(header);
    return NAME_LIKE_COLUMN_TERMS.some((term) => normalized.includes(term));
  });
}

function getUniqueHeaders(rawHeaders) {
  const seen = new Map();
  return rawHeaders.map((header, index) => {
    const base = String(header ?? "").trim() || `Column ${index + 1}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count ? `${base} ${count + 1}` : base;
  });
}

function rowHasValue(row) {
  return row.some((value) => String(value ?? "").trim() !== "");
}

function buildPreviewRows(headers, rawRows) {
  return buildRows(headers, rawRows.slice(0, 10));
}

function buildRows(headers, rawRows) {
  return rawRows.map((row, index) => {
    const record = { __rowId: index + 1 };
    headers.forEach((header, columnIndex) => {
      record[header] = String(row[columnIndex] ?? "").trim();
    });
    return record;
  });
}

function getSuggestedMapping(headers) {
  const mapping = {};
  STANDARD_FIELDS.forEach((field) => {
    const suggestedHeader = headers.find((header) => {
      const normalized = normalizeHeader(header);
      return FIELD_SUGGESTIONS[field.key].some(
        (term) => normalized === term || normalized.includes(term)
      );
    });
    mapping[field.key] = suggestedHeader ?? "";
  });
  return mapping;
}

function normalizeExhibitors(headers, rows, mapping) {
  const columnIndexes = Object.fromEntries(
    Object.entries(mapping).map(([field, header]) => [field, headers.indexOf(header)])
  );
  const valueAt = (row, field) => {
    const index = columnIndexes[field];
    return index >= 0 ? String(row[index] ?? "").trim() : "";
  };

  return rows
    .map((row, index) => ({
      id: `${Date.now()}-${index}`,
      companyName: valueAt(row, "companyName"),
      website: valueAt(row, "website"),
      boothNumber: valueAt(row, "boothNumber"),
      city: valueAt(row, "city"),
      state: valueAt(row, "state"),
      country: valueAt(row, "country"),
      industry: valueAt(row, "industry"),
      description: valueAt(row, "description"),
    }))
    .filter((exhibitor) => exhibitor.companyName.trim());
}

async function parseExhibitorFile(file) {
  const fileType = getFileType(file);
  let workbook;

  if (fileType === "CSV") {
    const text = await file.text();
    if (!text.trim()) {
      throw new Error("The selected file is empty.");
    }
    workbook = XLSX.read(text.replace(/^\uFEFF/, ""), { type: "string" });
  } else if (fileType === "XLSX") {
    if (!file.size) {
      throw new Error("The selected file is empty.");
    }
    const data = await file.arrayBuffer();
    workbook = XLSX.read(data, { type: "array" });
  } else {
    throw new Error("Select a CSV or XLSX exhibitor file.");
  }

  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) {
    throw new Error("The selected file is empty.");
  }

  const worksheet = workbook.Sheets[sheetName];
  const tableRows = XLSX.utils
    .sheet_to_json(worksheet, { header: 1, defval: "", blankrows: false })
    .filter(rowHasValue);

  if (tableRows.length < 2) {
    throw new Error("The selected file must include headers and at least one exhibitor row.");
  }

  const headers = getUniqueHeaders(tableRows[0]);
  if (!hasNameLikeColumn(headers)) {
    throw new Error("Add at least one company or name-like column before importing.");
  }

  const dataRows = tableRows.slice(1).filter(rowHasValue);
  if (!dataRows.length) {
    throw new Error("The selected file does not contain exhibitor rows.");
  }
  if (dataRows.length > MAX_IMPORT_ROWS) {
    throw new Error(
      `This list has ${dataRows.length.toLocaleString()} rows. Import up to ${MAX_IMPORT_ROWS.toLocaleString()} exhibitors at a time.`
    );
  }

  return {
    headers,
    rows: dataRows,
    previewRows: buildPreviewRows(headers, dataRows),
    totalRows: dataRows.length,
  };
}

export default function UploadExhibitorListModal({ open, onClose, onImport }) {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedPreview, setParsedPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [parseError, setParseError] = useState("");
  const [mappingError, setMappingError] = useState("");
  const [isParsing, setIsParsing] = useState(false);

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setParsedPreview(null);
    setColumnMapping({});
    setParseError("");
    setMappingError("");
    setIsParsing(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleClose = () => {
    clearSelectedFile();
    onClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setParsedPreview(null);
    setColumnMapping({});
    setParseError("");
    setMappingError("");

    if (!file) return;

    setIsParsing(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 0));
      const parsed = await parseExhibitorFile(file);
      setParsedPreview(parsed);
      setColumnMapping(getSuggestedMapping(parsed.headers));
    } catch (error) {
      setParseError(error?.message || "Parsing failed. Check the file and try again.");
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = () => {
    if (!selectedFile || !parsedPreview) return;
    if (!columnMapping.companyName) {
      setMappingError("Map a Company Name column before importing.");
      return;
    }

    const exhibitors = normalizeExhibitors(parsedPreview.headers, parsedPreview.rows, columnMapping);
    if (!exhibitors.length) {
      setMappingError("The mapped Company Name column does not contain any exhibitor names.");
      return;
    }

    const importPayload = {
      fileName: selectedFile.name,
      fileType: getFileType(selectedFile),
      fileSize: selectedFile.size,
      headers: parsedPreview.headers,
      mapping: columnMapping,
      previewRows: parsedPreview.previewRows,
      exhibitors,
      totalRows: parsedPreview.totalRows,
    };
    clearSelectedFile();
    onImport?.(importPayload);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1.5px solid ${BRAND_BLUE}`,
          boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid #e8edf4",
          pb: 2,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            background: BRAND_BLUE,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <UploadFileIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, color: BRAND_BLUE, fontSize: "1rem", flex: 1 }}>
          Upload Exhibitor List
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#8a9ab0" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          onClick={(event) => {
            event.currentTarget.value = "";
          }}
          style={{ display: "none" }}
        />
        <Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          sx={{
            border: "1.5px dashed #c5d0de",
            borderRadius: 2,
            py: 3,
            px: 3,
            textAlign: "center",
            backgroundColor: "#f7f9fc",
            cursor: "pointer",
            transition: "border-color 0.15s ease, background-color 0.15s ease",
            "&:hover": {
              borderColor: BRAND_BLUE,
              backgroundColor: "rgba(0,51,108,0.04)",
            },
          }}
        >
          <UploadFileIcon
            sx={{ fontSize: 40, color: selectedFile ? BRAND_BLUE : "#b0bec5", mb: 1.5 }}
          />
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#5a6a80", mb: 0.5 }}>
            Select CSV or XLSX file
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "#8a9ab0" }}>
            Choose an exhibitor list from your computer.
          </Typography>
        </Box>

        {parseError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {parseError}
          </Alert>
        )}

        {mappingError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mappingError}
          </Alert>
        )}

        {selectedFile && (
          <Box
            sx={{
              mt: 2.5,
              border: "1px solid #e0e8f0",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <UploadFileIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: "#26384d",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedFile.name}
              </Typography>
              <Chip
                label={parsedPreview ? "Ready to import" : isParsing ? "Parsing" : "Needs attention"}
                size="small"
                sx={{
                  height: 22,
                  backgroundColor: parsedPreview ? "rgba(100,131,21,0.12)" : "rgba(183,121,31,0.12)",
                  color: parsedPreview ? BRAND_GREEN : "#B7791F",
                  fontWeight: 700,
                  fontSize: 11,
                }}
              />
            </Box>
            <Divider sx={{ borderColor: "#edf1f6" }} />
            <Box
              sx={{ px: 2, py: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#8a9ab0", mb: 0.25 }}>
                  File type
                </Typography>
                <Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: "#3a4a5c" }}>
                  {getFileType(selectedFile)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#8a9ab0", mb: 0.25 }}>
                  File size
                </Typography>
                <Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: "#3a4a5c" }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {isParsing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, color: "#5a6a80" }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }}>
              Parsing exhibitor file...
            </Typography>
          </Box>
        )}

        {parsedPreview && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: BRAND_BLUE, mb: 1 }}>
                Map columns
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {STANDARD_FIELDS.map((field) => (
                  <FormControl key={field.key} size="small" fullWidth required={field.key === "companyName"}>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={columnMapping[field.key] ?? ""}
                      onChange={(event) => {
                        setMappingError("");
                        setColumnMapping((prev) => ({
                          ...prev,
                          [field.key]: event.target.value,
                        }));
                      }}
                    >
                      <MenuItem value="">
                        <em>Not mapped</em>
                      </MenuItem>
                      {parsedPreview.headers.map((header) => (
                        <MenuItem key={`${field.key}-${header}`} value={header}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: BRAND_BLUE }}>
                Preview rows
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "#7a8fa8", fontWeight: 600 }}>
                Showing {parsedPreview.previewRows.length} of {parsedPreview.totalRows} rows
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
              {parsedPreview.headers.map((header) => (
                <Chip
                  key={header}
                  label={header}
                  size="small"
                  sx={{
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: "rgba(0,51,108,0.08)",
                    color: BRAND_BLUE,
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                />
              ))}
            </Box>
            <TableContainer
              sx={{
                maxHeight: 280,
                border: "1px solid #e0e8f0",
                borderRadius: 2,
                backgroundColor: "#ffffff",
              }}
            >
              <Table stickyHeader size="small" aria-label="Exhibitor file preview">
                <TableHead>
                  <TableRow>
                    {parsedPreview.headers.map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: 800,
                          color: BRAND_BLUE,
                          backgroundColor: "#f7f9fc",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedPreview.previewRows.map((row) => (
                    <TableRow key={row.__rowId}>
                      {parsedPreview.headers.map((header) => (
                        <TableCell
                          key={`${row.__rowId}-${header}`}
                          sx={{ color: "#3a4a5c", maxWidth: 220, whiteSpace: "nowrap" }}
                        >
                          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row[header] || "-"}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e8edf4" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: BRAND_BLUE,
            borderColor: BRAND_BLUE,
            "&:hover": { backgroundColor: "rgba(0,51,108,0.05)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!parsedPreview || isParsing}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            backgroundColor: BRAND_GREEN,
            "&:hover": { backgroundColor: "#507011" },
          }}
        >
          Import Preview Rows
        </Button>
      </DialogActions>
    </Dialog>
  );
}
